// The route registry: every area's routes (areas/<wp>/routes.tsx) put into
// the shell their path belongs to, each page loaded lazily, a placeholder for
// screens not built yet, and the redirects from the v1/v2 addresses.
// Foundation-owned: work packages edit their own routes.tsx, never this file.
import { lazy, type ComponentType, type ReactElement } from "react";
import type { RouteObject } from "react-router";
import { PAGES, pageLink } from "@convenor/shared";
import { routes as accessRoutes } from "../areas/access/routes";
import { routes as activitiesRoutes } from "../areas/activities/routes";
import { routes as adminRoutes } from "../areas/admin/routes";
import { routes as cmRoutes } from "../areas/cm/routes";
import { routes as eventsRoutes } from "../areas/events/routes";
import { routes as expertsRoutes } from "../areas/experts/routes";
import { routes as helpRoutes } from "../areas/help/routes";
import { routes as projectsRoutes } from "../areas/projects/routes";
import { routes as stakeholdersRoutes } from "../areas/stakeholders/routes";
import { routes as staffRoutes } from "../areas/staff/routes";
import { routes as workforceRoutes } from "../areas/workforce/routes";
import { routes as collabRoutes } from "../areas/collab/routes";
import { routes as ogsmRoutes } from "../areas/ogsm/routes";
import { routes as insightRoutes } from "../areas/insight/routes";
import { routes as tasksRoutes } from "../areas/tasks/routes";
import { ProjectScope } from "../shell/ProjectScope";
import { RequireAuth } from "../shell/RequireAuth";
import { AppShell, CmShell, FunderShell, GuestShell, KioskShell, OnboardingShell, PortalShell, PrintShell, PublicShell } from "../shell/Shells";
import { NotFoundContent } from "../ui/QueryStates";
import { isOrgAreaPath, OrgAreaGate } from "../shell/OrgAreaGate";
import { COVERAGE, type ShellKind } from "./coverage";
import { AccessibilityPage, PrivacyPage, TermsPage } from "./LegalPages";
import { Placeholder } from "./Placeholder";
import { LookupRedirect, Rename, TeamInviteGate, ToCurrentProject } from "./redirects";
import type { AreaRoute } from "./types";

/**
 * All area routes. Order matters only when two areas list the same path: the
 * first one wins (activities owns /p/:projectId/activities/:id, shared with events).
 */
export const AREA_ROUTES: AreaRoute[] = [
  ...accessRoutes,
  ...projectsRoutes,
  ...activitiesRoutes,
  ...eventsRoutes,
  ...expertsRoutes,
  ...stakeholdersRoutes,
  ...adminRoutes,
  ...helpRoutes,
  ...cmRoutes,
  ...staffRoutes,
  ...workforceRoutes,
  ...collabRoutes,
  ...ogsmRoutes,
  ...insightRoutes,
  ...tasksRoutes,
];

/** Paths registered twice (a mistake unless the screens say so). */
export function duplicatePaths(list: AreaRoute[] = AREA_ROUTES): string[] {
  const seen = new Set<string>();
  const dups: string[] = [];
  for (const r of list) {
    if (seen.has(r.path)) dups.push(r.path);
    seen.add(r.path);
  }
  return dups;
}

/** COVERAGE rows no area registered (should be empty). */
export function missingScreens(list: AreaRoute[] = AREA_ROUTES): number[] {
  const have = new Set(list.flatMap((r) => r.screens));
  return COVERAGE.filter((row) => !have.has(row.n)).map((row) => row.n);
}

// One React.lazy per route, made once (never during render).
const lazyPages = new Map<AreaRoute, ComponentType>();
for (const r of AREA_ROUTES) if (r.load) lazyPages.set(r, lazy(r.load));

function elementFor(r: AreaRoute): ReactElement {
  if (r.redirectTo) return <Rename to={r.redirectTo} />;
  const Page = lazyPages.get(r);
  const el = Page ? <Page /> : <Placeholder screens={r.screens} links={r.links} />;
  // Old team-invitation links used /invite/:token too.
  if (r.path === PAGES.invite) return <TeamInviteGate>{el}</TeamInviteGate>;
  return el;
}

const PROJECT_PREFIX: string = PAGES.projectHome;

function toRoute(r: AreaRoute): RouteObject {
  if (r.path === PROJECT_PREFIX) return { index: true, element: elementFor(r) };
  if (r.path.startsWith(`${PROJECT_PREFIX}/`)) return { path: r.path.slice(PROJECT_PREFIX.length + 1), element: elementFor(r) };
  return { path: r.path, element: elementFor(r) };
}

function byShell(kind: ShellKind, filter: (r: AreaRoute) => boolean = () => true): RouteObject[] {
  const seen = new Set<string>();
  return AREA_ROUTES.filter((r) => (r.shell ?? "app") === kind && filter(r))
    .filter((r) => (seen.has(r.path) ? false : (seen.add(r.path), true)))
    .map(toRoute);
}

const tor = (versions: boolean) => (r: { projectId: string | null }, p: Record<string, string | undefined>) =>
  versions
    ? pageLink("torVersion", { projectId: r.projectId ?? "", id: p.id ?? "", version: p.version ?? "" })
    : pageLink("tor", { projectId: r.projectId ?? "", id: p.id ?? "" });

/** Redirects from the old addresses (API-v3 13) that need a signed-in person. */
const LEGACY_APP_ROUTES: RouteObject[] = [
  { index: true, element: <ToCurrentProject /> },
  { path: "/contracts", element: <Rename to={PAGES.projects} /> },
  { path: "/contracts/new", element: <Rename to={PAGES.projectsNew} /> },
  { path: "/contracts/:id", element: <Rename to={PAGES.adminOverview} ids={{ projectId: "id" }} /> },
  { path: "/contracts/:id/edit", element: <Rename to={PAGES.contractEdit} ids={{ projectId: "id" }} /> },
  { path: "/contracts/:id/tors/new", element: <Rename to={PAGES.torsNew} ids={{ projectId: "id" }} /> },
  { path: "/contracts/:id/logframe", element: <Rename to={PAGES.logframe} ids={{ projectId: "id" }} /> },
  { path: "/tors/:id", element: <LookupRedirect entity="tor" to={tor(false)} /> },
  { path: "/tors/:id/versions/:version", element: <LookupRedirect entity="tor" to={tor(true)} /> },
  { path: "/tors/:id/print", element: <Rename to={PAGES.printTor} /> },
  { path: "/activity", element: <ToCurrentProject page="activityLog" /> },
  { path: "/experts", element: <ToCurrentProject page="experts" /> },
  { path: "/experts/:id", element: <ToCurrentProject page="expert" /> },
  { path: "/timesheets", element: <ToCurrentProject page="expertsTimesheets" /> },
  { path: "/events", element: <ToCurrentProject page="activities" query="type=event" /> },
  { path: "/events/:id", element: <LookupRedirect entity="event" to={(r, p) => pageLink("activity", { projectId: r.projectId ?? "", id: p.id ?? "" })} /> },
  { path: "/events/:id/check-in", element: <LookupRedirect entity="event" to={(r, p) => pageLink("activityCheckIn", { projectId: r.projectId ?? "", id: p.id ?? "" })} /> },
  { path: "/network", element: <ToCurrentProject page="stakeholders" /> },
  { path: "/network/people/:id", element: <ToCurrentProject page="stakeholder" /> },
  { path: "/deliverables", element: <ToCurrentProject page="activities" query="type=deliverable" /> },
  { path: "/reports/:id", element: <LookupRedirect entity="report" to={(r, p) => pageLink("report", { projectId: r.projectId ?? "", id: p.id ?? "" })} /> },
  { path: "/analytics", element: <ToCurrentProject page="analytics" /> },
];

const LEGACY_PUBLIC_ROUTES: RouteObject[] = [
  { path: "/login", element: <Rename to={PAGES.signIn} /> },
  { path: "/signup", element: <Rename to={PAGES.signUpTeam} /> },
  { path: "/logout", element: <Rename to={PAGES.signOut} /> },
];

const LEGAL_ROUTES: RouteObject[] = [
  { path: "/privacy", element: <PrivacyPage /> },
  { path: "/terms", element: <TermsPage /> },
  { path: "/accessibility", element: <AccessibilityPage /> },
];

/** The whole tree, under the root layout given by App.tsx. */
export function buildRoutes(): RouteObject[] {
  const isPublicWelcome = (r: AreaRoute) => r.path === PAGES.welcome || r.path === PAGES.welcomeAccount;
  return [
    { element: <PublicShell />, children: [...byShell("public"), ...LEGAL_ROUTES, ...LEGACY_PUBLIC_ROUTES] },
    {
      element: <OnboardingShell />,
      children: [...byShell("onboarding", isPublicWelcome), { element: <RequireAuth allowNoOrg />, children: byShell("onboarding", (r) => !isPublicWelcome(r)) }],
    },
    {
      element: <RequireAuth allowNoOrg />,
      children: [{ element: <AppShell />, children: byShell("app", (r) => r.allowNoOrg === true) }],
    },
    {
      element: <RequireAuth />,
      children: [
        {
          element: <AppShell />,
          children: [
            ...byShell("app", (r) => r.allowNoOrg !== true && !isOrgAreaPath(r.path)),
            // People and organisation: "Page not found" unless the organisation has it switched on.
            { element: <OrgAreaGate />, children: byShell("app", (r) => r.allowNoOrg !== true && isOrgAreaPath(r.path)) },
            { path: PROJECT_PREFIX, element: <ProjectScope />, children: [...byShell("project"), { path: "*", element: <NotFoundContent /> }] },
            ...LEGACY_APP_ROUTES,
            { path: "*", element: <NotFoundContent /> },
          ],
        },
      ],
    },
    { element: <RequireAuth allowNoOrg />, children: [{ element: <PortalShell />, children: byShell("portal") }] },
    { element: <FunderShell />, children: byShell("funder") },
    // The contract manager has an account but usually no organisation of their own.
    { element: <RequireAuth allowNoOrg signIn={PAGES.cmSignIn} />, children: [{ element: <CmShell />, children: byShell("cm") }] },
    { element: <GuestShell />, children: byShell("guest") },
    { element: <KioskShell />, children: byShell("kiosk") },
    { element: <RequireAuth allowNoOrg />, children: [{ element: <PrintShell />, children: byShell("print") }] },
  ];
}
